import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api'
import { ROLE_LABELS } from '../lib/utils'
import drinxLogo from '../assets/drinx_logo_white.png'

const schema = z.object({
  name: z.string().min(1, 'Required'),
  password: z.string().min(12, 'Minimum 12 characters'),
  confirm: z.string(),
}).refine(d => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] })
type FormData = z.infer<typeof schema>

interface TokenInfo { email: string; role: string }

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading')
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [showPw, setShowPw] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (!token) { setTokenState('invalid'); return }
    api.get(`/auth/validate-token?token=${token}&type=invite`)
      .then(res => { setTokenInfo({ email: res.data.email, role: res.data.role }); setTokenState('valid') })
      .catch(() => setTokenState('invalid'))
  }, [token])

  async function onSubmit(data: FormData) {
    setApiError(null)
    try {
      const res = await api.post('/auth/accept-invite', { token, name: data.name, password: data.password })
      const { token: jwt, user } = res.data
      localStorage.setItem('admin_token', jwt)
      localStorage.setItem('admin_user', JSON.stringify(user))
      navigate('/dashboard', { replace: true })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setApiError(err.response?.data?.message ?? 'Failed to set up account')
    }
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-1">
            <img src={drinxLogo} alt="DrinX" className="h-10 w-auto" />
          </div>
          <p className="text-gray-500 text-sm">Admin Portal</p>
        </div>

        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-6">
          {tokenState === 'loading' && (
            <div className="text-center py-4 text-gray-500 text-sm">Verifying invitation…</div>
          )}

          {tokenState === 'invalid' && (
            <div className="text-center space-y-2 py-2">
              <p className="text-sm font-medium text-white">Invalid invitation</p>
              <p className="text-sm text-gray-400">This invitation link is invalid or has expired. Ask your admin to send a new invite.</p>
            </div>
          )}

          {tokenState === 'valid' && tokenInfo && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-white mb-1">Set up your account</h2>
                <p className="text-sm text-gray-500">
                  You've been invited as{' '}
                  <span className="text-gray-300">{ROLE_LABELS[tokenInfo.role] ?? tokenInfo.role}</span>.
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Email</label>
                <div className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-gray-400 select-none">
                  {tokenInfo.email}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Full name</label>
                <input
                  {...register('name')}
                  autoFocus
                  className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
                  placeholder="Your full name"
                />
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPw ? 'text' : 'password'}
                    className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 pr-10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
                    placeholder="Min. 12 characters"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Confirm password</label>
                <input
                  {...register('confirm')}
                  type={showPw ? 'text' : 'password'}
                  className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
                  placeholder="••••••••••••"
                />
                {errors.confirm && <p className="text-xs text-red-400 mt-1">{errors.confirm.message}</p>}
              </div>

              {apiError && (
                <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{apiError}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
              >
                {isSubmitting ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
